// NOTICE: KAPTION-INTEGRATION
// rumo-app-updates.FLAG.1-2, CHECK.1-3, DESKTOP.1-4
//
// Two different things make the Rumo someone has open out of date, and they are
// fixed in different ways:
//
//   Refresh: production started serving a newer web bundle. A page keeps running
//   the bundle it loaded until it reloads, and pages live long: a tab left open
//   all week, or the Rumo Desktop window, which only hides when it is closed.
//
//   Update: Rumo Desktop downloaded a newer version of the app itself. It installs
//   on restart, and restarting also loads the newest web bundle.
//
// Refresh never asks Rails. The build writes /bundle-version.json into the image
// and bakes the same version into this bundle as VITE_RUMO_BUNDLE_VERSION (see
// .github/workflows/_base.build.yml). Because the file ships inside the image, it
// changes when an environment starts *serving* a new image. Production is
// deployed by hand, often hours after the build, so anything published at build
// time would ask people to refresh into the bundle they already run.
//
// State lives at module scope: the full and the collapsed sidebar both render the
// mark, and switching between them must neither lose nor repeat anything.
import { computed, ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export const BUNDLE_VERSION_URL = '/bundle-version.json';
// Out of the way of boot: nothing about the first minute needs this.
export const FIRST_CHECK_DELAY = 30 * 1000;
export const CHECK_INTERVAL = 10 * 60 * 1000;
export const MIN_CHECK_GAP = 60 * 1000;
// rumo-app-updates.CHECK.2 — production replaces its replicas one at a time
// (about 3 minutes for 8), and every replacement drops the websockets it held.
// A new version read this long after the page's last disruption (its own load,
// or a websocket reconnect) comes from a rollout that has finished, so a refresh
// lands on the new bundle instead of an old replica.
export const CONFIRM_AFTER = 4 * 60 * 1000;
export const LATER_SNOOZE = 4 * 60 * 60 * 1000;

const readRunningVersion = () => import.meta.env.VITE_RUMO_BUNDLE_VERSION || '';

// Exposed as an object so specs can observe the reload without navigating jsdom.
export const pageReloader = {
  reload: () => window.location.reload(),
};

const emptyBundle = () => ({
  pending: null,
  pendingSince: 0,
  available: false,
});

const bundle = ref(emptyBundle());
const desktop = ref(null);
const snoozed = ref(false);
const busy = ref(null);

let lastCheckAt = 0;
let started = false;
// The page's own load is its first disruption: it may have loaded mid-rollout.
let lastDisruptionAt = Date.now();
let settleTimer = null;
let snoozeTimer = null;
let cleanups = [];

const desktopBridge = () => window.rumoDesktop?.updates;

/**
 * rumo-app-updates.FLAG.1-2 — production turns this on; a page with no baked
 * bundle version (local development) never checks.
 */
export const isBundleRefreshEnabled = () =>
  window.chatwootConfig?.kaptionBundleRefresh === true &&
  Boolean(readRunningVersion());

/**
 * Read again once a rollout that disrupted the page at `lastDisruptionAt` must
 * have finished. Mid-rollout the read that follows a reconnect often reaches a
 * replica still serving the old bundle; waiting for the next 10-minute check
 * after that is why a deploy could go unnoticed for a quarter of an hour.
 */
const scheduleSettledCheck = () => {
  clearTimeout(settleTimer);
  const wait = Math.max(0, lastDisruptionAt + CONFIRM_AFTER - Date.now());
  settleTimer = setTimeout(
    // eslint-disable-next-line no-use-before-define
    () => checkBundleVersion({ force: true }),
    wait + 5000
  );
};

/**
 * The websocket came back: usually a deploy replacing the replica it was on.
 */
export const noteConnectionDisruption = (at = Date.now()) => {
  lastDisruptionAt = at;
  scheduleSettledCheck();
};

/**
 * Apply one read of the version production serves.
 *
 * A new version is offered once it is read at least CONFIRM_AFTER after the
 * page's last disruption, so a tab coming back long after a deploy offers
 * Refresh on its first read, and a tab that lived through the rollout offers it
 * as soon as the rollout has settled. A read of the version this page already
 * runs changes nothing: mid-rollout it is just an old replica answering, so it
 * must not cancel a pending new version.
 */
export const applyBundleRead = (version, now = Date.now()) => {
  if (!version || version === readRunningVersion()) return;

  const settled = now - lastDisruptionAt >= CONFIRM_AFTER;
  const current = bundle.value;
  if (current.pending !== version) {
    bundle.value = { pending: version, pendingSince: now, available: settled };
    if (!settled) scheduleSettledCheck();
    return;
  }

  if (!current.available && settled) {
    bundle.value = { ...current, available: true };
  }
};

/**
 * rumo-app-updates.CHECK.1, CHECK.3 — read /bundle-version.json past every
 * cache (Rails serves public/ files with a year-long max-age). Any failure just
 * leaves things as they were for the next check.
 */
export const checkBundleVersion = async ({ force = false } = {}) => {
  if (!isBundleRefreshEnabled()) return;
  const now = Date.now();
  if (!force && now - lastCheckAt < MIN_CHECK_GAP) return;
  lastCheckAt = now;

  try {
    const response = await fetch(`${BUNDLE_VERSION_URL}?t=${now}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!response.ok) return;
    const body = await response.json();
    if (typeof body?.version !== 'string') return;
    // Only `version` is compared. `commit` changes with every build, including
    // backend-only ones that serve the same bundle, so comparing it would ask
    // people to refresh into the bundle they already run (rumo-app-updates.BUILD.3).
    applyBundleRead(body.version, Date.now());
  } catch {
    // Offline, a replica restarting, a malformed body: the next check retries.
  }
};

export const setDesktopUpdateState = state => {
  desktop.value = state && typeof state === 'object' ? state : null;
  // The app refused to restart (nothing was ready after all): give the mark back.
  if (busy.value === 'restart' && desktop.value?.phase !== 'ready') {
    busy.value = null;
  }
};

const startWatching = () => {
  if (started) return;
  started = true;

  // rumo-app-updates.DESKTOP.1
  const bridge = desktopBridge();
  if (bridge) {
    Promise.resolve()
      .then(() => bridge.getState?.())
      .then(setDesktopUpdateState)
      .catch(() => {});
    const unsubscribe = bridge.onStateChange?.(setDesktopUpdateState);
    if (typeof unsubscribe === 'function') cleanups.push(unsubscribe);
  }

  if (!isBundleRefreshEnabled()) return;

  const check = () => checkBundleVersion();
  // rumo-app-updates.CHECK.1 — read right away, and again once the rollout
  // behind the reconnect has settled.
  const onReconnect = () => {
    noteConnectionDisruption();
    check();
  };
  const checkWhenVisible = () => {
    if (document.visibilityState !== 'hidden') check();
  };
  const firstCheck = setTimeout(check, FIRST_CHECK_DELAY);
  const interval = setInterval(checkWhenVisible, CHECK_INTERVAL);
  document.addEventListener('visibilitychange', checkWhenVisible);
  window.addEventListener('focus', check);
  // Every deploy restarts the replicas holding the websockets, so a reconnect is
  // the earliest hint that production may serve something new.
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, onReconnect);

  cleanups.push(
    () => clearTimeout(firstCheck),
    () => clearInterval(interval),
    () => document.removeEventListener('visibilitychange', checkWhenVisible),
    () => window.removeEventListener('focus', check),
    () => emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, onReconnect)
  );
};

/**
 * What the Rumo mark should say, one state at a time.
 *
 * update beats refresh: restarting the app also loads the newest bundle
 * (rumo-app-updates.DESKTOP.2). A failed routine check says nothing; only a
 * failed download of a known version does (DESKTOP.4).
 */
const mode = computed(() => {
  if (busy.value) return 'busy';
  const app = desktop.value;
  if (app?.phase === 'ready') return 'update';
  if (bundle.value.available) return 'refresh';
  if (app?.phase === 'downloading') return 'downloading';
  if (app?.phase === 'error' && app.availableVersion) return 'error';
  return 'idle';
});

const refreshNow = () => {
  busy.value = 'refresh';
  pageReloader.reload();
};

const restartToUpdate = async () => {
  const bridge = desktopBridge();
  if (!bridge?.restartToInstall) return;
  busy.value = 'restart';
  try {
    const restarting = await bridge.restartToInstall();
    if (restarting === false) busy.value = null;
  } catch {
    busy.value = null;
  }
};

const retryUpdate = () => {
  const bridge = desktopBridge();
  if (!bridge?.checkForUpdates) return;
  Promise.resolve()
    .then(() => bridge.checkForUpdates())
    .catch(() => {});
};

// rumo-app-updates.MARK.5
const later = () => {
  snoozed.value = true;
  clearTimeout(snoozeTimer);
  snoozeTimer = setTimeout(() => {
    snoozed.value = false;
  }, LATER_SNOOZE);
};

export function useKaptionAppUpdates() {
  startWatching();

  return {
    mode,
    busyAction: computed(() => busy.value),
    isSnoozed: computed(() => snoozed.value),
    availableVersion: computed(() => desktop.value?.availableVersion || ''),
    currentVersion: computed(() => desktop.value?.currentVersion || ''),
    downloadPercent: computed(() => {
      const percent = Number(desktop.value?.percent);
      if (!Number.isFinite(percent)) return 0;
      return Math.min(100, Math.max(0, Math.round(percent)));
    }),
    refreshNow,
    restartToUpdate,
    retryUpdate,
    later,
  };
}

export const resetKaptionAppUpdatesForTests = () => {
  cleanups.forEach(cleanup => cleanup());
  cleanups = [];
  started = false;
  lastCheckAt = 0;
  lastDisruptionAt = 0;
  clearTimeout(settleTimer);
  clearTimeout(snoozeTimer);
  bundle.value = emptyBundle();
  desktop.value = null;
  snoozed.value = false;
  busy.value = null;
};
